/**
 * Mutation hook for creating a location tag
 *
 * @see GitLab #999 — Support for adding location tags
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '../../services/api';
import {
  getOfflineLocationTagList,
  isDuplicateLocationTagName,
  mirrorLocationTagsToOfflineCache,
  nextLocationTagDraftId,
  runSerializedLocationTagOfflineMutation,
} from '../../services/locationTagOfflineSync';
import { networkService } from '../../services/network';
import { offlineCache } from '../../services/offlineCache';
import { locationTagsKeys } from '../queries/useLocationTags';

import type {
  CreateLocationTagInput,
  LocationTag,
  LocationTagResponse,
} from '../../types/locationTag';

interface CreateLocationTagParams {
  requestId: string;
  data: CreateLocationTagInput;
}

export const useCreateLocationTag = () => {
  const queryClient = useQueryClient();

  return useMutation({
    networkMode: 'always',
    mutationFn: async ({ requestId, data }: CreateLocationTagParams) => {
      const trimmedName = data.name.trim();
      if (!trimmedName) {
        throw new Error('Location tag name is required');
      }

      if (!networkService.isConnected()) {
        return runSerializedLocationTagOfflineMutation(async () => {
          const existingTags = await getOfflineLocationTagList(requestId);
          if (isDuplicateLocationTagName(existingTags, trimmedName)) {
            throw new Error(
              `Location tag name "${trimmedName}" is already used in this request. Tag names must be unique.`,
            );
          }

          const tempId = nextLocationTagDraftId(existingTags);
          const requestIdNum = parseInt(requestId, 10);
          const newTag: LocationTag = {
            id: tempId,
            requestId: requestIdNum,
            name: trimmedName,
          };
          await offlineCache.addLocationTagToOfflineQueue(
            requestId,
            'create',
            { name: trimmedName },
            tempId,
          );
          await mirrorLocationTagsToOfflineCache(requestId, [
            ...existingTags,
            newTag,
          ]);
          return newTag;
        });
      }

      const response = await api.post<LocationTagResponse>(
        `/requests/${requestId}/location-tags`,
        { name: trimmedName },
      );
      await mirrorLocationTagsToOfflineCache(requestId, [
        ...(await offlineCache.getCachedLocationTags(requestId)),
        response.locationTag,
      ]);
      return response.locationTag;
    },
    onSuccess: (_tag, { requestId }) => {
      queryClient.invalidateQueries({
        queryKey: locationTagsKeys.list(requestId),
      });
    },
    meta: {
      errorMessage: 'Failed to create location tag. Please try again.',
    },
  });
};
